import React, { useState, useEffect, useRef } from "react";
import { Play, Clock, X, Check, CheckCircle, ChevronDown, Share2, Users, BadgeCheck, Sparkles, FolderPlus} from "lucide-react";
import { DrillVariationSheet, VARIATION_FIELDS, VARIATION_ORDER, loadMyVariations } from "./DrillVariationSheet";
import { GOLD } from "../constants/theme";
import { CollectionMenu } from "../shared/FavLists";
import { isTheaterViewport } from "./VideoTheater";
import { SECTION_COLORS } from "../constants/navigation";
import { LOGO } from "../constants/branding";
import { useDoubleTap } from "../../utils/useGestures";
import { haptic as bamHaptic, nativeShare as bamNativeShare } from "../../utils/nativeBridge";
import { Ic } from "../constants/icons";
import { Pop } from "../shared/motion";
import { useCoaches } from "../hooks/useCoaches";
import { logEvent } from "../utils/analytics";
// MuxPlayer goes through LazyMuxPlayer, which lazy-loads the video stack
// (hls.js + media-chrome + @mux/* — ~3MB) into its own async chunk rather
// than the main boot bundle. Briefly eager-imported once to dodge a web
// stale-chunk ChunkLoadError, but reverted: the true cost was ~3MB (not the
// ~150kb assumed) and parsing it on boot was the main iOS 26 WKWebView crash
// risk. Native ships chunks locally (can't 404); web's residual stale-chunk
// case is handled by BAMFull's ChunkLoadError boundary + the version pill.
import MuxPlayer from "../components/LazyMuxPlayer";
import { shownKeyPoints } from "./keyPoints";

// Map a content section to the analytics event that signals a coach
// "touched" that feature. Used by the open + play tracking below so the
// Intelligence Feature Adoption room can pivot per-section instead of
// per-content_id. Sections not in the map fall back to a generic
// content_open event.
const SECTION_OPEN_EVENT = {
  pd:      "drill_opened",
  team:    "drill_opened",
  ti:      "drill_opened",       // team insights = drill-style content
  xo:      "drill_opened",
  master:  "masterclass_opened",
  workouts:"drill_opened",
  plans:   "plan_template_opened",
};

// Memoized: the drills grid renders 24+ of these inside ContentPage, whose
// state changes on every search keystroke and filter toggle — without memo
// the whole grid re-rendered per character. ContentPage passes identity-
// stable onClick handlers so the shallow compare holds.
export const ContentCard = React.memo(function ContentCard({item, sectionId, C, dark, onClick, favDrills=[], toggleFav, masterProgress, isAiMatch=false, dim=false, watched=false, onWatchedThreshold, onToggleWatched, onOpenTheater, favLists=[], memberLists=[], onToggleList, onCreateList, rosterNames=[]}){
  const [listMenu, setListMenu] = useState(false);
  const thumbColors = SECTION_COLORS[sectionId] || SECTION_COLORS.pd;
  const thumbColor = thumbColors[item.id % thumbColors.length];
  const coaches = useCoaches();
  const coach = item.coachId ? coaches?.byId?.[item.coachId] : null;
  const isFav = favDrills.some(f=>f.key===`${sectionId}-${item.id}`);
  const [thumbErr,setThumbErr]=useState(false);
  const [vidLoading,setVidLoading]=useState(true);
  // Double-tap → toggle bookmark (Instagram pattern). Single tap still
  // opens the card. Flashes a quick heart via the saved animation.
  const [doubleTapFlash, setDoubleTapFlash] = useState(false);
  const dt = useDoubleTap({
    onSingleTap: () => {
      // Fire feature-adoption event before delegating. Generic content_open
      // is the fallback so coverage stays complete for new section ids.
      try {
        const evt = SECTION_OPEN_EVENT[sectionId] || "content_open";
        logEvent(evt, { kind: sectionId, content_id: item.id, title: item.title });
      } catch {}
      onClick(item);
    },
    onDoubleTap: () => {
      if (toggleFav) toggleFav(item, sectionId);
      setDoubleTapFlash(true);
      setTimeout(() => setDoubleTapFlash(false), 700);
    },
    enabled: !!toggleFav,
  });
  const [playingMux,setPlayingMux]=useState(false);
  const hasMux = !!item.muxPlaybackId;
  // Video progress tracking: fire video_progress_25/75/completed at the
  // appropriate thresholds. State refs (not useState) so the player's
  // timeupdate callback doesn't trigger React re-renders.
  const videoProgressRef = useRef({ p25:false, p50:false, p75:false, ended:false });
  // Prompt 4: NEW badge only for items added within 2 days
  const isRecentlyNew = item.isNew && item.date_added && (Date.now()-new Date(item.date_added).getTime()<2*86400000);
  const showNew = item.isNew && !item.date_added || isRecentlyNew; // fallback: show if no date_added field
  // Pre-release modules — render a "Drops {date}" pill on the cover so
  // coaches know the card is queued, not broken or coming-soon-forever.
  const dropTs = item.availableAt ? new Date(item.availableAt).getTime() : 0;
  const hasDropDate = dropTs && dropTs > Date.now();
  // Pre-release covers a dated drop OR a dateless `comingSoon` item (no
  // release date set yet). Dated ones show "DROPS {date}"; dateless ones
  // show "COMING SOON".
  const isPreRelease = item.comingSoon || hasDropDate;
  const dropLabel = hasDropDate
    ? new Date(dropTs).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;
  // Prompt 8: masterclass progress bar
  const mProg = sectionId==="master" && masterProgress ? masterProgress[`master-${item.id}`] : null;
  return (
    <div className="cc" onClick={dt.onClick}
      style={{
        background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:14,overflow:"hidden",
        boxShadow:`${C.surfaceShadow}, ${C.innerGlow}`,"--hs":C.elevatedShadow,position:"relative",
        // Coming-soon dimming — when this is a pre-release item being
        // shown in the regular Library grid, lower the card's prominence
        // so it reads as "queued" not "active". The DROPS pill stays
        // gold on top to keep the date legible. When the user is on the
        // Coming Soon tab specifically, dim=false so the cards render
        // at full strength.
        ...(dim && isPreRelease ? { opacity: 0.55, filter: "saturate(0.6)" } : null),
        // Watched dimming. Lighter than the coming-soon treatment
        // above, because these are the one kind of dim card a coach
        // might actually want to open: "the one I watched last week"
        // still has to be findable, not greyed into the background.
        ...(watched && !isPreRelease ? { opacity: 0.62 } : null),
      }}>
      {/* The mark is a control, not a badge. Cards autoplay muted
          inline, so a coach who clicks one and walks away gets marked
          watched without watching - this is how they take it back, and
          how someone who skims a drill in ten seconds sinks it. */}
      {onToggleWatched && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggleWatched(item); }}
          aria-pressed={watched}
          aria-label={watched ? `Mark ${item.title} as not watched` : `Mark ${item.title} as watched`}
          title={watched ? "Watched. Click to undo" : "Mark as watched"}
          className={`cc-watched-btn${watched ? " is-on" : ""}`}
          style={{position:"absolute",top:8,left:8,zIndex:6,height:24,padding:"0 8px",
            display:"inline-flex",alignItems:"center",gap:4,borderRadius:12,cursor:"pointer",
            border:`1px solid ${watched ? GOLD + "99" : "rgba(255,255,255,0.22)"}`,
            background: watched ? "rgba(8,9,11,0.78)" : "rgba(8,9,11,0.5)",
            color: watched ? GOLD : "rgba(255,255,255,0.85)",
            fontSize:9,fontWeight:800,letterSpacing:.8,textTransform:"uppercase",
            backdropFilter:"blur(4px)"}}>
          <Check size={10} strokeWidth={3}/>{watched ? "Watched" : ""}
        </button>
      )}
      {/* Double-tap bookmark flash — Instagram-style heart pulse on
          the card when the user double-taps. Pure visual feedback;
          the actual toggleFav fires from useDoubleTap. */}
      {doubleTapFlash && (
        <div style={{position:"absolute",inset:0,zIndex:10,pointerEvents:"none",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{
            background: `radial-gradient(circle, ${GOLD}30 0%, ${GOLD}00 65%)`,
            width: "70%", height: "70%",
            display: "flex", alignItems: "center", justifyContent: "center",
            animation: "bamHeartBurst .65s ease-out forwards",
          }}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill={GOLD} stroke={GOLD} strokeWidth="0">
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z"/>
            </svg>
          </div>
        </div>
      )}
      {/* Thumbnail / Video */}
      <div style={{height:playingMux?220:130,background:thumbErr||(!item.thumb&&!hasMux)?"#0A0A0A":hasMux?"#000":thumbColor,position:"relative",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",
        border:thumbErr&&!hasMux?`1px solid ${GOLD}30`:"none",transition:"height .2s ease"}}>
        {/* Inline Mux Player (when user clicked play on a Mux-enabled card) */}
        {playingMux && hasMux ? (
          <>
            <MuxPlayer
              playbackId={item.muxPlaybackId}
              streamType="on-demand"
              autoPlay
              accentColor={GOLD}
              metadata={{video_title:item.title}}
              onPlay={() => {
                try { logEvent("video_played", { content_id: item.id, title: item.title, kind: sectionId }); } catch {}
              }}
              onTimeUpdate={(e) => {
                const v = e?.target; if (!v || !v.duration) return;
                const pct = (v.currentTime / v.duration) * 100;
                const ref = videoProgressRef.current;
                if (!ref.p25 && pct >= 25) {
                  ref.p25 = true;
                  try { logEvent("video_progress_25", { content_id: item.id, title: item.title }); } catch {}
                }
                if (!ref.p50 && pct >= 50) {
                  ref.p50 = true;
                  // Halfway is the line for "watched". It is generous on
                  // purpose: a coach who gets half of a drill has taken
                  // what they came for, and the explicit control above
                  // exists for the cases this gets wrong.
                  try { onWatchedThreshold?.(item, Math.round(pct)); } catch {}
                }
                if (!ref.p75 && pct >= 75) {
                  ref.p75 = true;
                  try { logEvent("video_progress_75", { content_id: item.id, title: item.title }); } catch {}
                }
              }}
              onEnded={() => {
                const ref = videoProgressRef.current;
                if (ref.ended) return;
                ref.ended = true;
                try { logEvent("video_completed", { content_id: item.id, title: item.title, kind: sectionId }); } catch {}
                // A short drill can end before timeupdate ever reports
                // 50%, so completion has to mark it too.
                try { onWatchedThreshold?.(item, 100); } catch {}
              }}
              muted
              playsInline
              style={{position:"absolute",inset:0,width:"100%",height:"100%"}}
            />
            <button onClick={e=>{e.stopPropagation();setPlayingMux(false);}} aria-label="Close player"
              style={{position:"absolute",top:6,right:6,zIndex:5,width:36,height:36,borderRadius:18,background:"rgba(0,0,0,0.6)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <X size={14} color="#fff"/>
            </button>
          </>
        ) : (
          <>
            {/* Static thumbnail (Mux auto-thumbnail or custom thumb or fallback) */}
            {(hasMux || (item.thumb && !thumbErr)) ? (
              <>
                <img
                  src={hasMux ? `https://image.mux.com/${item.muxPlaybackId}/thumbnail.jpg?width=480` : item.thumb}
                  alt={item.title}
                  onError={()=>setThumbErr(true)}
                  onLoad={e=>{e.currentTarget.style.opacity=1;}}
                  loading="lazy"
                  decoding="async"
                  style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",opacity:0,transition:"opacity .3s ease"}}
                />
                <div style={{position:"absolute",inset:0,background:"linear-gradient(transparent 40%,rgba(0,0,0,0.55))"}}/>
                <div onClick={e=>{
                    if(!hasMux) return;
                    e.stopPropagation();
                    // A 220px tile is the smallest view we have. On a
                    // screen with room, play goes to the big player;
                    // on a phone the card is most of the width anyway.
                    if (onOpenTheater && isTheaterViewport()) onOpenTheater(item);
                    else setPlayingMux(true);
                  }}
                  style={{position:"relative",zIndex:1,width:40,height:40,borderRadius:"50%",background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",cursor:hasMux?"pointer":"default"}}>
                  <Play size={16} color="#fff" fill="#fff"/>
                </div>
              </>
            ) : thumbErr ? (
              <img src={LOGO} alt="BAM" className="micro-pulse" style={{width:36,height:42,objectFit:"contain",opacity:.5,filter:`drop-shadow(0 0 8px ${GOLD}33)`}}/>
            ) : (
              <div style={{width:44,height:44,borderRadius:"50%",background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <Play size={18} color="#fff" fill="#fff"/>
              </div>
            )}
          </>
        )}
        {/* Pre-release supersedes NEW — if a module hasn't dropped yet we
            don't also call it "new", just signal when it lands. */}
        {isPreRelease ? (
          <div style={{position:"absolute",top:10,left:10,fontSize:9,fontWeight:800,color:GOLD,background:"rgba(0,0,0,0.78)",border:`1px solid ${GOLD}80`,padding:"3px 9px",borderRadius:4,letterSpacing:.8,display:"inline-flex",alignItems:"center",gap:5,boxShadow:`0 0 18px ${GOLD}33`}}>
            <span style={{width:6,height:6,borderRadius:"50%",background:GOLD,boxShadow:`0 0 6px ${GOLD}`}}/>
            {dropLabel ? `DROPS ${dropLabel.toUpperCase()}` : "COMING SOON"}
          </div>
        ) : (
          <>
            {showNew&&<div className="new-badge" style={{position:"absolute",top:10,left:10,fontSize:9,fontWeight:800,color:"#1A1A1A",background:GOLD,padding:"3px 8px",borderRadius:4,letterSpacing:.8}}>NEW</div>}
            {item.free&&<div style={{position:"absolute",top:showNew?34:10,left:10,fontSize:9,fontWeight:800,color:GOLD,background:"rgba(0,0,0,0.72)",border:`1px solid ${GOLD}66`,padding:"3px 8px",borderRadius:4,letterSpacing:.8}}>FREE</div>}
            {item.guest&&<div style={{position:"absolute",top:10,right:10,fontSize:9,fontWeight:800,color:GOLD,background:"rgba(0,0,0,0.72)",border:`1px solid ${GOLD}66`,padding:"3px 8px",borderRadius:4,letterSpacing:.8}}>GUEST</div>}
          </>
        )}
        <div onClick={e=>{e.stopPropagation();bamHaptic("light");toggleFav&&toggleFav(item,sectionId);}}
          title={isFav ? "Remove bookmark" : "Bookmark this drill"}
          style={{position:"absolute",top:8,right:9,width:28,height:28,borderRadius:"50%",
            background:isFav?"rgba(226,221,159,0.35)":"rgba(0,0,0,0.55)",
            display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",
            transition:"transform .15s, background .15s",zIndex:2,
            border:isFav?`1px solid ${GOLD}`:"1px solid rgba(255,255,255,0.15)"}}
          className={`btn${!isFav ? " bam-bookmark-hint" : ""}`}>
          <Pop on={isFav}><Ic.Bookmark c={isFav?GOLD:"#fff"} f={isFav} s={13}/></Pop>
        </div>
        {/* Group it into a collection without leaving the grid.
            Bookmarking and grouping used to be two gestures in two places:
            save here, then go to the dashboard and sort. A member asked for
            exactly this ("as I'm scanning through the database"), so picking
            a collection saves the drill too when it isn't saved yet. One
            decision, one gesture. */}
        {onToggleList && (
          <div onClick={e=>{e.stopPropagation();setListMenu(o=>!o);}}
            title="Add to a collection"
            style={{position:"absolute",top:8,right:43,width:28,height:28,borderRadius:"50%",
              background:memberLists.length?"rgba(226,221,159,0.35)":"rgba(0,0,0,0.55)",
              display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",
              transition:"transform .15s, background .15s",zIndex:2,
              border:memberLists.length?`1px solid ${GOLD}`:"1px solid rgba(255,255,255,0.15)"}}
            className="btn">
            <FolderPlus size={13} color={memberLists.length?GOLD:"#fff"}/>
          </div>
        )}
        {listMenu && onToggleList && (
          <CollectionMenu C={C} dark={dark} lists={favLists} memberLists={memberLists}
            suggestions={rosterNames} align="right" top={42}
            onPick={(listId)=>{ onToggleList(listId); }}
            onCreate={(name)=>{ onCreateList?.(name); }}
            onClose={()=>setListMenu(false)}/>
        )}
        {memberLists.length > 0 && (
          <div style={{position:"absolute",top:42,right:9,display:"flex",flexDirection:"column",
            alignItems:"flex-end",gap:3,zIndex:1,maxWidth:"70%"}}>
            {memberLists.slice(0,2).map(id=>{
              const l=favLists.find(x=>x.id===id);
              if(!l) return null;
              return (
                <span key={id} style={{fontSize:9,fontWeight:800,color:GOLD,
                  background:"rgba(0,0,0,0.7)",border:`1px solid ${GOLD}55`,padding:"2px 7px",
                  borderRadius:8,letterSpacing:.3,maxWidth:"100%",overflow:"hidden",
                  textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.name}</span>
              );
            })}
            {memberLists.length>2 && (
              <span style={{fontSize:9,fontWeight:800,color:GOLD,background:"rgba(0,0,0,0.7)",
                border:`1px solid ${GOLD}55`,padding:"2px 7px",borderRadius:8}}>
                +{memberLists.length-2}
              </span>
            )}
          </div>
        )}
        <div style={{position:"absolute",bottom:8,right:10,display:"flex",alignItems:"center",gap:4,background:"rgba(0,0,0,0.5)",borderRadius:5,padding:"3px 8px"}}>
          <Clock size={10} color="#fff"/>
          <span style={{fontSize:10,color:"#fff",fontWeight:600}}>{item.duration}</span>
        </div>
        {isAiMatch && (
          <div title="Matched by concept (semantic search)"
            style={{position:"absolute",bottom:8,left:10,display:"flex",alignItems:"center",gap:4,background:"rgba(0,0,0,0.55)",border:`1px solid ${GOLD}66`,borderRadius:5,padding:"3px 8px"}}>
            <Sparkles size={10} color={GOLD}/>
            <span style={{fontSize:9,color:GOLD,fontWeight:800,letterSpacing:.6,textTransform:"uppercase"}}>Smart Match</span>
          </div>
        )}
      </div>
      {/* Info */}
      <div style={{padding:"13px 14px"}}>
        <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:7,flexWrap:"wrap"}}>
          <div style={{fontSize:9,fontWeight:700,color:"#111",background:GOLD,padding:"2px 8px",borderRadius:10,letterSpacing:.5}}>{item.tag || item.category}</div>
          <div style={{fontSize:10,color:C.textDim,fontWeight:500}}>{item.level}</div>
          {coach && (
            <div title={`${coach.name}${coach.isVerified ? " (verified)" : ""}`}
              style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:9.5,color:C.textMid,fontWeight:600,marginLeft:"auto"}}>
              {coach.isVerified && <BadgeCheck size={11} color={GOLD}/>}
              <span style={{maxWidth:100,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{coach.name}</span>
            </div>
          )}
        </div>
        <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:4,lineHeight:1.3,
          display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{item.title}</div>
        {/* Subtitle hidden on phones to reduce density — class is media-
            gated so desktop still shows it. */}
        <div className="bam-content-header" style={{fontSize:12,color:C.textDim,marginBottom:10}}>{item.sub}</div>
      </div>
      {/* Prompt 8: masterclass progress bar */}
      {mProg>0&&<div style={{position:"absolute",bottom:0,left:0,right:0,height:3,background:C.border}}>
        <div className="bam-fill" style={{height:"100%",width:`${Math.min(mProg,100)}%`,background:GOLD,borderRadius:"0 2px 0 0"}}/>
      </div>}
    </div>
  );
});

export function ContentDetail({item, sectionId, C, dark, onClose, masterProgress, setMasterProgress, videoNotes={}, saveVideoNotes, onWatchedThreshold, onOpenTheater, favDrills=[], toggleFav, favLists=[], memberLists=[], onToggleList, onCreateList, rosterNames=[]}){
  const [listMenu, setListMenu] = useState(false);
  // What this coach has already written about this drill, so the chips show
  // filled and their own words come back to them on the card.
  const [myVars, setMyVars] = useState({});
  const [varSheet, setVarSheet] = useState(null);
  // "Make it yours" starts folded away. Five chips and a subtitle is a lot of
  // furniture to show someone who hasn't written anything yet, and the drill
  // itself is what they came for. It unfolds on tap, and stays unfolded for
  // anyone who has already written on this drill: at that point it isn't a
  // prompt any more, it's their work, and their work shouldn't be hidden.
  const [yoursOpen, setYoursOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const writtenCount = VARIATION_ORDER.filter(k => myVars[k]).length;
  useEffect(() => {
    let live = true;
    if (!item?.id) return;
    loadMyVariations(item.id).then(v => {
      if (!live) return;
      setMyVars(v);
      if (VARIATION_ORDER.some(k => v[k])) setYoursOpen(true);
    });
    return () => { live = false; };
  }, [item?.id]);
  const thumbColors = SECTION_COLORS[sectionId] || SECTION_COLORS.pd;
  const thumbColor = thumbColors[item.id % thumbColors.length];
  // Prompt 4: persist saved/liked state in localStorage
  /* Bookmarking from this panel used to write a localStorage flag
     (`bam_fav_<section>_<id>`) and nothing else. The card's bookmark writes
     `fav_drills` in user_data through toggleFav. So they were two unrelated
     systems for one action: a drill saved from in here never appeared in My
     Bookmarks, never reached her phone, and went away with site data, while
     the same drill's card showed the opposite state.

     Now this is the same bookmark as everywhere else. The old flag is read
     once and promoted, so anything saved from this panel before today gets
     recovered rather than quietly discarded. */
  const legacyFavKey = `bam_fav_${sectionId}_${item.id}`;
  const favKey = `${sectionId}-${item.id}`;
  const saved = favDrills.some(f => f.key === favKey);
  useEffect(() => {
    if (!toggleFav || saved) return;
    try {
      if (localStorage.getItem(legacyFavKey) === "1") {
        localStorage.removeItem(legacyFavKey);
        toggleFav(item, sectionId);
      }
    } catch { /* storage unavailable, nothing to recover */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacyFavKey]);
  const toggleSaved = () => { toggleFav?.(item, sectionId); };
  // Prompt 8: masterclass progress
  const mKey=`master-${item.id}`;
  const mProg=sectionId==="master"&&masterProgress?masterProgress[mKey]||0:0;
  const hasKeyPoints=shownKeyPoints(item).length>0;
  const handleClose=()=>{ onClose(); };
  const hasMuxDetail = !!item.muxPlaybackId;
  const [detailPlaying,setDetailPlaying]=useState(false);
  // Refs, not state: the player fires timeupdate several times a
  // second and none of this should cost a render.
  const detailProgressRef = useRef({ p50:false, ended:false });
  // Play, from the panel. On a screen with room this hands off to the
  // theater; the 340px panel is for reading and notes, not for seeing
  // whether a foot landed inside or outside. Below the breakpoint the
  // panel is already the whole screen, so it plays in place.
  const playDetail = () => {
    if (onOpenTheater && isTheaterViewport()) onOpenTheater(item);
    else setDetailPlaying(true);
  };
  // Heart pulses continuously while the detail is open AND the item isn't
  // already saved. The moment they favorite it, the pulse stops (no point
  // drawing attention to a solved action).
  const pulseHeart = !saved;
  // Mark masterclass as started when detail is opened
  useEffect(()=>{
    if(sectionId==="master"&&setMasterProgress&&mProg===0){
      setMasterProgress(prev=>{const n={...prev,[mKey]:10};try{localStorage.setItem("bam_master_progress",JSON.stringify(n));}catch{}return n;});
    }
  },[]);// eslint-disable-line
  return (
    <div className="detail-panel" style={{width:340,flexShrink:0,background:C.cardGradient,borderLeft:`1px solid ${C.cardBorder}`,display:"flex",flexDirection:"column",overflow:"auto",boxShadow:dark?"-4px 0 24px rgba(0,0,0,0.3)":"none"}}>
      {/* Prompt 8: masterclass progress bar at top */}
      {sectionId==="master"&&mProg>0&&(
        <div style={{height:3,background:C.border,flexShrink:0}}>
          <div className="bam-fill" style={{height:"100%",width:`${Math.min(mProg,100)}%`,background:GOLD}}/>
        </div>
      )}
      {/* Video / Thumbnail */}
      <div style={{height:detailPlaying&&hasMuxDetail?240:180,background:hasMuxDetail?"#000":thumbColor,position:"relative",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden",transition:"height .2s ease"}}>
        {detailPlaying && hasMuxDetail ? (
          <MuxPlayer
            playbackId={item.muxPlaybackId}
            streamType="on-demand"
            autoPlay
            accentColor={GOLD}
            metadata={{video_title:item.title}}
            /* This player tracked nothing at all until now, which meant
               the one place a coach actually sits and watches a drill
               through was the one place we learned nothing from. */
            onTimeUpdate={(e) => {
              const v = e?.target; if (!v || !v.duration) return;
              const pct = (v.currentTime / v.duration) * 100;
              const ref = detailProgressRef.current;
              if (!ref.p50 && pct >= 50) {
                ref.p50 = true;
                try { onWatchedThreshold?.(item, Math.round(pct)); } catch {}
              }
            }}
            onEnded={() => {
              const ref = detailProgressRef.current;
              if (ref.ended) return;
              ref.ended = true;
              try { onWatchedThreshold?.(item, 100); } catch {}
            }}
            style={{position:"absolute",inset:0,width:"100%",height:"100%"}}
          />
        ) : (
          <>
            {(hasMuxDetail || item.thumb) && (
              <>
                <img src={hasMuxDetail ? `https://image.mux.com/${item.muxPlaybackId}/thumbnail.jpg?width=600` : item.thumb} alt={item.title} loading="lazy" decoding="async"
                  style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover"}}/>
                <div style={{position:"absolute",inset:0,background:"linear-gradient(transparent 30%,rgba(0,0,0,0.5))"}}/>
              </>
            )}
            <div onClick={hasMuxDetail ? playDetail : undefined}
              style={{position:"relative",zIndex:1,width:54,height:54,borderRadius:"50%",background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)",cursor:hasMuxDetail?"pointer":"default"}}>
              <Play size={22} color="#fff" fill="#fff"/>
            </div>
          </>
        )}
        <button onClick={handleClose} style={{position:"absolute",top:12,right:12,zIndex:5,width:28,height:28,borderRadius:"50%",background:"rgba(0,0,0,0.5)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)"}}>
          <X size={13} color="#fff"/>
        </button>
      </div>

      {/* Prompt 7: Workout leave confirmation modal */}

      <div style={{padding:20,flex:1}}>
        {/* Prompt 8: Continue where you left off — starts the video.
            Only rendered when there's a playable Mux video; a CTA that
            can't act shouldn't exist (got reported as "broken"). */}
        {sectionId==="master"&&mProg>0&&mProg<100&&hasMuxDetail&&!detailPlaying&&(
          <div className="btn" onClick={playDetail}
            style={{display:"flex",alignItems:"center",gap:8,padding:"10px 14px",background:`${GOLD}12`,border:`1px solid ${GOLD}30`,borderRadius:9,marginBottom:14,cursor:"pointer"}}>
            <Play size={13} color={GOLD} fill={GOLD}/>
            <span style={{fontSize:12,fontWeight:700,color:GOLD}}>Continue where you left off ({mProg}%)</span>
          </div>
        )}
        {/* Tags row */}
        <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:12}}>
          <div style={{fontSize:10,fontWeight:700,color:"#111",background:GOLD,padding:"3px 9px",borderRadius:10,letterSpacing:.4}}>{item.tag || item.category}</div>
          <div style={{fontSize:11,color:C.textDim}}>{item.level}</div>
          <div style={{display:"flex",alignItems:"center",gap:4,marginLeft:"auto",fontSize:11,color:C.textDim}}>
            <Clock size={11} color={C.textDim}/>{item.duration}
          </div>
        </div>

        {/* Title */}
        <div style={{fontSize:17,fontWeight:800,color:C.text,marginBottom:4,lineHeight:1.3}}>{item.title}</div>
        {/* sub is usually the description cut at a character limit ("...with a spac"),
            so it only shows when it says something the description doesn't. */}
        {item.sub && !(item.desc||"").startsWith(String(item.sub).trim().slice(0,40)) && (
          <div style={{fontSize:12,color:C.textDim,marginBottom:14}}>{item.sub}</div>
        )}

        {/* Notes: the description and key points, folded. Coleman, 2026-10-19:
            hidden unless the coach wants them, with a two-line preview so the
            drill still says what it is. Search tags aren't shown at all; they
            still feed search. */}
        {(item.desc||hasKeyPoints)&&(
          <div style={{marginTop:10}}>
            {item.desc&&(
              <div style={{fontSize:13,color:C.textMid,lineHeight:1.7,marginBottom:notesOpen?16:8,
                ...(notesOpen?{}:{display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"})}}>{item.desc}</div>
            )}
            {notesOpen&&hasKeyPoints&&(
              <>
                <div style={{fontSize:12,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.8,marginBottom:10}}>Key Points</div>
                {item.keyPoints.map((pt,i)=>(
                  <div key={i} style={{display:"flex",gap:9,alignItems:"flex-start",marginBottom:8}}>
                    <CheckCircle size={13} color={GOLD} style={{flexShrink:0,marginTop:2}}/>
                    <div style={{fontSize:13,color:C.textMid,lineHeight:1.55}}>{pt}</div>
                  </div>
                ))}
              </>
            )}
            {(hasKeyPoints||(item.desc||"").length>110)&&<button className="btn" onClick={()=>setNotesOpen(o=>!o)} aria-expanded={notesOpen}
              style={{display:"inline-flex",alignItems:"center",gap:5,padding:0,marginTop:2,background:"transparent",border:"none",
                cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:800,color:C.textMid}}>
              {notesOpen?"Hide notes":"Show notes"}
              <ChevronDown size={14} color={C.textDim} style={{transform:notesOpen?"rotate(180deg)":"none",transition:"transform .24s ease"}}/>
            </button>}
          </div>
        )}

        {/* Make this drill yours — the same five fields as a Learning Lab
            session, writing to the same drill_variations rows. A coach who
            marks up a drill while browsing has done the identical thing as
            one who did it inside a session, so it had better be the identical
            component. */}
        <div style={{marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
          <button className="btn" onClick={()=>setYoursOpen(o=>!o)} aria-expanded={yoursOpen}
            style={{width:"100%",display:"flex",alignItems:"center",gap:8,padding:0,
              background:"transparent",border:"none",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
            <span style={{fontSize:12,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.8}}>Make it yours</span>
            {writtenCount>0&&(
              <span style={{fontSize:11,fontWeight:700,color:GOLD}}>{writtenCount}</span>
            )}
            <ChevronDown size={15} color={C.textDim} style={{marginLeft:"auto",
              transform:yoursOpen?"rotate(180deg)":"none",transition:"transform .24s ease"}}/>
          </button>
          <div style={{overflow:"hidden",maxHeight:yoursOpen?1400:0,opacity:yoursOpen?1:0,
            transition:"max-height .3s ease, opacity .22s ease"}}>
            <div style={{fontSize:11.5,color:C.textDim,lineHeight:1.5,margin:"9px 0 11px"}}>
              Anything you write here is yours to keep, and shows up on this drill next time.
            </div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {VARIATION_ORDER.map(k=>{
                const written=!!myVars[k];
                return (
                  <button key={k} className="btn" onClick={()=>setVarSheet(k)}
                    tabIndex={yoursOpen?0:-1}
                    title={written?myVars[k]:VARIATION_FIELDS[k].hint}
                    style={{display:"flex",alignItems:"center",gap:5,padding:"7px 11px",borderRadius:8,
                      cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:700,whiteSpace:"nowrap",
                      background:written?`${GOLD}18`:"transparent",color:written?GOLD:C.textMid,
                      border:`1px solid ${written?GOLD+"55":C.border}`}}>
                    {written&&<Check size={11}/>}{VARIATION_FIELDS[k].chip}
                  </button>
                );
              })}
            </div>
            {/* What they've already written, so the drill carries their thinking
                rather than making them remember they had any. */}
            {VARIATION_ORDER.filter(k=>myVars[k]).map(k=>(
              <div key={k} style={{marginTop:10,paddingLeft:11,borderLeft:`2px solid ${GOLD}44`}}>
                <div style={{fontSize:9.5,fontWeight:800,letterSpacing:1,textTransform:"uppercase",color:C.textDim,marginBottom:3}}>{VARIATION_FIELDS[k].label}</div>
                <div style={{fontSize:12.5,color:C.textMid,lineHeight:1.55}}>{myVars[k]}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions.
            No "Watch Now", and no "Start Workout" either. The player is
            already at the top of this panel, so a full-width gold bar
            that scrolls you back up to it was the loudest thing on the
            page while doing the least.

            "Start Workout" looked like more than that and wasn't. It
            played the video, renamed itself "In Progress...", and made
            closing the panel warn "your progress will be reset" - over
            a boolean that reset on close. Nothing was tracked, nothing
            was saved. A warning about progress that does not exist is
            worse than no button, and it was the reason workouts were
            excluded from the big player. Now they open in it like
            everything else.

            If workout progress becomes real - drill_views and the 50%
            mark are most of the way there - this comes back with
            something behind it. */}
        <div style={{display:"flex",gap:9,marginTop:18,justifyContent:"flex-end"}}>
          <div className="btn" onClick={toggleSaved}
            title={saved ? "Remove bookmark" : "Bookmark this drill"}
            style={{width:44,height:44,borderRadius:9,border:`1px solid ${saved?GOLD:C.border}`,background:saved?C.goldDim:"transparent",display:"flex",alignItems:"center",justifyContent:"center",
              animation:pulseHeart?"bamHeartPulse 1.4s ease-in-out infinite":"none"}}>
            <Pop on={saved}><Ic.Bookmark c={saved?GOLD:C.textDim} f={saved} s={17}/></Pop>
          </div>
          {onToggleList && (
            <div style={{position:"relative"}}>
              <div className="btn" onClick={()=>setListMenu(o=>!o)}
                title="Add to a collection"
                style={{width:44,height:44,borderRadius:9,
                  border:`1px solid ${memberLists.length?GOLD:C.border}`,
                  background:memberLists.length?C.goldDim:"transparent",
                  display:"flex",alignItems:"center",justifyContent:"center"}}>
                <FolderPlus size={17} color={memberLists.length?GOLD:C.textDim}/>
              </div>
              {listMenu && (
                <CollectionMenu C={C} dark={dark} lists={favLists} memberLists={memberLists}
                  suggestions={rosterNames} align="right" top={50}
                  onPick={(listId)=>{ if(!saved) toggleFav?.(item, sectionId); onToggleList(listId); }}
                  onCreate={(name)=>{ if(!saved) toggleFav?.(item, sectionId); onCreateList?.(name); }}
                  onClose={()=>setListMenu(false)}/>
              )}
            </div>
          )}
          <div className="btn" onClick={async (e)=>{
            e.stopPropagation();
            const url = `${window.location.origin}/#/${sectionId}/${item.id}`;
            await bamNativeShare({
              title: item.name || item.title || "Drill on BAM",
              text: (item.summary || item.description || "").slice(0, 200),
              url,
              dialogTitle: "Share drill",
            });
          }} style={{width:44,height:44,borderRadius:9,border:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
            <Share2 size={15} color={C.textDim}/>
          </div>
        </div>
        {/* My Notes */}
        {(()=>{
          const noteKey=`${sectionId}-${item.id}`;
          const notes=videoNotes[noteKey]||[];
          return (
            <div style={{marginTop:16,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
              <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:10}}>
                <span style={{fontSize:12,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.8}}>My Notes</span>
                {notes.length>0&&<span style={{fontSize:11,color:C.textDim}}>{notes.length}</span>}
              </div>
              {notes.map((note,i)=>(
                <div key={i} style={{display:"flex",gap:8,marginBottom:8,alignItems:"flex-start"}}>
                  <div className="btn" style={{fontSize:10,fontWeight:700,color:GOLD,background:GOLD+"15",padding:"2px 7px",borderRadius:6,flexShrink:0,marginTop:2}}>{note.time}</div>
                  <div style={{fontSize:12,color:C.textMid,lineHeight:1.55,flex:1}}>{note.text}</div>
                  <div className="btn" onClick={()=>{const updated=notes.filter((_,j)=>j!==i);saveVideoNotes&&saveVideoNotes(noteKey,updated);}} style={{flexShrink:0,marginTop:2}}><X size={10} color={C.textDim}/></div>
                </div>
              ))}
              {/* One field, no button. Enter saves, which is what everyone
                  tries first, and a gold plus next to a text box was a second
                  primary action on a page that already had too many. */}
              <input placeholder="Take a note, then press Enter" onKeyDown={e=>{
                if(e.key==="Enter"&&e.target.value.trim()){
                  const newNote={text:e.target.value.trim(),time:"0:00"};
                  saveVideoNotes&&saveVideoNotes(noteKey,[...notes,newNote]);
                  e.target.value="";
                }
              }} style={{width:"100%",padding:"8px 11px",borderRadius:8,border:`1px solid ${C.border}`,background:dark?"#111":"#f5f5f5",color:C.text,fontSize:12,outline:"none",fontFamily:"'DM Sans',sans-serif",boxSizing:"border-box"}}/>
            </div>
          );
        })()}

        {/* Share to Community */}
        <div className="btn" onClick={()=>{
          window.location.hash="#/community";
          // Pre-fill compose with embedded content — handled via custom event
          setTimeout(()=>{
            window.dispatchEvent(new CustomEvent("bam-share-content",{detail:{id:item.id,sectionId,title:item.title,tag:item.tag,duration:item.duration,muxPlaybackId:item.muxPlaybackId||item.mux_playback_id||null}}));
          },300);
        }} style={{display:"flex",alignItems:"center",justifyContent:"center",gap:7,marginTop:10,padding:"10px 0",borderRadius:9,border:`1px solid ${C.border}`,background:dark?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)",fontSize:13,fontWeight:600,color:C.textMid}}>
          <Users size={14} color={C.textDim}/> Share to Community
        </div>
      </div>
      {varSheet && (
        <DrillVariationSheet C={C} drill={item} kind={varSheet}
          existing={myVars[varSheet] || ""}
          onClose={() => setVarSheet(null)}
          onSaved={(k, body) => setMyVars(v => ({ ...v, [k]: body }))}/>
      )}
    </div>
  );
}
